// ============================================================================
// src/pages/Projects.js
// -----------------------------------------------------------------------------
// Page principale : gestion des projets (affichage, création, édition, suppression)
// Gère aussi les modales d’information et de gestion des membres
// ============================================================================

import React, { useState, useEffect } from "react";
import '../styles/main.css';
import theme from "../styles/theme";

// ============================================================================
// 🔹 Import des composants réutilisables
// ============================================================================
import PageHeader from "../components/reusable/ModuleHeader/PageHeader";
import Breadcrumb from "../components/reusable/ModuleHeader/Breadcrumb";
import Button from "../components/reusable/Forms/Button";
import CardProject from "../components/reusable/Card/CardProject";
import ProjectInfoManage from "../components/defined/project_manage/ProjectInfoManage";
import ProjectUserManage from "../components/defined/project_manage/ProjectUserManage";
import Switch from "../components/reusable/Forms/Switch";
import TextFilter from "../components/reusable/Forms/TextFilter";

// ============================================================================
// 🔹 Import routage API
// ============================================================================
import { API_ROUTES } from "../config/api";
import{apiFetch} from "../config/apiFetch";

// ============================================================================
// 🔸 Composant principal : ProjectsSubpage
// ============================================================================
const ProjectsSubpage = () => {

  // --------------------------------------------------------------------------
  // 🧩 États principaux
  // --------------------------------------------------------------------------
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // ========================================================================
  // 🔹 Chargement des données depuis l’API
  // ========================================================================

  // --- Chargement global des projets ---
  const loadProjects = async (signal) => {
    try {
      setLoading(true);
      setError(null);

      const res = await apiFetch(API_ROUTES.projects.getAll, { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const list = await res.json();
      setProjects(list);
    } catch (e) {
      if (!signal?.aborted) setError("Erreur lors du chargement des projets");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  // --- Chargement initial ---
  useEffect(() => {
    const ac = new AbortController();
    loadProjects(ac.signal);
    return () => ac.abort();
  }, []);

  // --- Rechargement après action (création, suppression, etc.) ---
  const refreshProjects = async () => {
    try {
      setError(null);

      const res = await apiFetch(API_ROUTES.projects.getAll);
      if (!res.ok) throw new Error("Erreur de rechargement");

      const list = await res.json();
      setProjects(list);
    } catch (err) {
      console.error("Erreur refresh :", err);
      setError("Erreur lors du rechargement des projets");
    }
  };

  // ========================================================================
  // 🔹 Actions utilisateur (création, édition, gestion, suppression)
  // ========================================================================

  // --- Ouverture modale création ---
  const openCreate = () => {
    setIsEditMode(false);
    setSelectedProject(null);
    setIsModalOpen(true);
  };

  // --- Ouverture modale édition ---
  const openEdit = (project) => {
    setIsEditMode(true);
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  // --- Ouverture modale gestion des membres ---
  const openManage = (project) => {
    setSelectedProject(project);
    setIsManageOpen(true);
  };

  // --- Suppression d’un projet ---
  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce projet ?")) return;
    try {
      const res = await apiFetch(API_ROUTES.projects.byId(id), { method: "DELETE" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setProjects((prev) => prev.filter((p) => p.id !== id));
    } catch {
      alert("Suppression impossible");
    }
  };

  // ========================================================================
  // 🔹 Filtrage local des projets
  // ========================================================================
  const filteredProjects = projects
    .filter((p) => showArchived || !p.archived)
    .filter((p) =>
      [p.name, p.location]
        .filter(Boolean)
        .some((field) =>
          field.toLowerCase().includes(searchTerm.trim().toLowerCase())
        )
    );

  // ========================================================================
  // 🔹 Rendu principal
  // ========================================================================
  return (
    <div style={{ padding: 30 }}>
      {/* En-tête de navigation */}
      <Breadcrumb items={[{ label: "Projects", href: "/" }]} />
      <PageHeader
        title="Gestion des projets"
        subtitle="Création, édition et suivi des projets"
      />
      <hr className="page-separator" />

      {/* Barre d’actions principales */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: theme.spacing.sm,
        }}
      >
        {/* Boutons à gauche */}
        <div style={{ display: "flex", alignItems: "center", gap: theme.spacing.md, marginLeft: "25px" }}>
          <Button variant="blue" onClick={openCreate}>
            Create
          </Button>
          <Button
            variant="red"
            onClick={() => selectedProject && handleDelete(selectedProject.id)}
            disabled={!selectedProject}
          >
            Delete
          </Button>
        </div>

        {/* Champ de recherche */}
        <div style={{ display: "flex", alignItems: "center", gap: theme.spacing.md, width:"800px" }}>
          <TextFilter
            label="Recherche"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Entrez le nom ou le lieu du projet"
          />
        </div>

        {/* Filtre archivés */}
        <div style={{ display: "flex", alignItems: "center", lineHeight: 1, marginRight: "25px",  }}>
          <Switch
            label="Projets archivés"
            enabled={showArchived}
            onChange={setShowArchived}
            showText={false}
            size="small"
            switchStyle={{ transform: "translateY(2px)" }}
          />
        </div>
      </div>

      {/* États de chargement ou d’erreur */}
      {loading && <p>Chargement des projets…</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}

      {/* Liste des projets */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
          gap: theme.spacing.lg,
        }}
      >
        {filteredProjects.map((p) => (
          <div
            key={p.id}
            onClick={() => setSelectedProject(p)}
            style={{
              border:
                selectedProject?.id === p.id
                  ? `2px solid ${theme.colors.text.primary.default}`
                  : "none",
              borderRadius: theme.borders.radius.md,
              cursor: "pointer",
            }}
          >
            <CardProject
              id={p.id}
              name={p.name}
              description={p.description}
              location={p.location}
              sector={p.sector}
              currency={p.currency}
              status={p.status}
              archived={p.archived}
              userCount={p.userCount ?? 0}
              admin={[p.ownerName].filter(Boolean)}
              onEdit={() => openEdit(p)}
              onManage={() => openManage(p)}
              isSelected={selectedProject?.id === p.id}
              onRefresh={refreshProjects}
            />
          </div>
        ))}
      </div>

      {/* Modales */}
      <ProjectInfoManage
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        mode={isEditMode ? "edit" : "create"}
        initialValues={isEditMode ? selectedProject : null}
        onSave={refreshProjects}
      />

      {selectedProject && (
        <ProjectUserManage
          isOpen={isManageOpen}
          onClose={() => setIsManageOpen(false)}
          projectId={selectedProject.id}
          userId={selectedProject.userId}
          onRefresh={refreshProjects}
        />
      )}
    </div>
  );
};

export default ProjectsSubpage;
